import React from 'react';
import {
  CalendarIcon,
  TrendingUp,
  Activity,
  PieChart,
  Package,
} from 'lucide-react';
import { CalendarEvent } from './types';
import { formatFinnishPercentage } from '@/utils';
import { formatDateWithOptions } from '@/utils/Date';

// Helper: format a Date to YYYY-MM-DD
const formatDateDay = (d: Date) => d.toISOString().substring(0, 10);
const formatPercent = (value: number) =>
  formatFinnishPercentage(value, { maximumFractionDigits: 0 });

interface StatusCardsProps {
  filteredEvents: CalendarEvent[];
  backendEvents: CalendarEvent[];
  availableBerthCount: number;
  availableAddonCount: number;
}

const StatusCards: React.FC<StatusCardsProps> = ({
  filteredEvents,
  backendEvents,
  availableBerthCount,
  availableAddonCount,
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      {/* Total Bookings Card */}
      <div className="group rounded-2xl shadow-lg hover:shadow-xl border border-gray-100 dark:border-gray-700 contrast:border-gray-700 overflow-hidden">
        <div className="bg-blue-100 dark:bg-blue-950 contrast:bg-blue-950 p-4">
          <div className="flex justify-between items-start">
            <div className="text-gray-900">
              <p className="text-gray-900 text-[16px] font-[500] ">
                Total Bookings
              </p>
              <h1 className="text-2xl font-bold mt-1 text-gray-900 ">
                {
                  filteredEvents.filter((e) => !e.isAvailable && !e.isBlocked)
                    .length
                }
              </h1>
            </div>
            <div className="p-3 bg-blue-300 dark:bg-blue-700 contrast:bg-blue-700 rounded-xl backdrop-blur-sm">
              <CalendarIcon className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>
        <div className="p-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600 contrast:text-gray-700">
              With current filters
            </span>
            <div className="flex items-center gap-1 text-blue-600 dark:text-blue-400 contrast:text-blue-400">
              <TrendingUp className="w-4 h-4 contrast:text-white" />
              <span className="font-medium contrast:text-white">Active</span>
            </div>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-600 contrast:bg-gray-600 h-2 rounded-full mt-3 overflow-hidden">
            <div
              className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all duration-500"
              style={{
                width:
                  backendEvents.length > 0
                    ? `${Math.min(100, (filteredEvents.filter((e) => !e.isAvailable && !e.isBlocked).length / backendEvents.length) * 100)}%`
                    : '0%',
              }}
            />
          </div>
        </div>
      </div>

      {/* Today's Bookings Card */}
      <div className="group rounded-2xl shadow-lg hover:shadow-xl border border-gray-100 dark:border-gray-700 contrast:border-gray-700 overflow-hidden">
        <div className="bg-blue-100 dark:bg-blue-950 contrast:bg-blue-950 p-4">
          <div className="flex justify-between items-start">
            <div className="text-gray-900">
              <p className="text-gray-900 text-[16px] font-[500]">
                Today's Bookings
              </p>
              <h2 className="text-2xl font-bold mt-1 text-gray-900">
                {
                  filteredEvents.filter(
                    (e) =>
                      e.start === formatDateDay(new Date()) &&
                      !e.isAvailable &&
                      !e.isBlocked
                  ).length
                }
              </h2>
            </div>
            <div className="p-3 bg-blue-300 dark:bg-blue-700 contrast:bg-blue-700 rounded-xl backdrop-blur-sm">
              <Activity className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>
        <div className="p-4">
          <div className="text-sm text-gray-600 contrast:text-gray-700">
            {formatDateWithOptions(new Date(), {
              weekday: 'long',
              month: 'short',
              day: 'numeric',
            }, 'en-US')}
          </div>
          <div className="flex items-center gap-2 mt-2">
            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
            <span className="text-xs text-emerald-800 dark:text-emerald-400 contrast:text-emerald-400 font-medium">
              Live Updates
            </span>
          </div>
        </div>
      </div>

      {/* Asset Distribution Card */}
      <div className="group rounded-2xl shadow-lg hover:shadow-xl border border-gray-100 dark:border-gray-700 contrast:border-gray-700 overflow-hidden">
        <div className="bg-blue-100 dark:bg-blue-950 contrast:bg-blue-950 p-4">
          <div className="flex justify-between items-start">
            <div className="text-gray-900">
              <p className="text-gray-900 text-[16px] font-[500]">
                Asset Distribution
              </p>
            </div>
            <div className="p-3 bg-blue-300 dark:bg-blue-700 contrast:bg-blue-700 rounded-xl backdrop-blur-sm">
              <PieChart className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>
        <div className="p-4">
          {(() => {
            const berthCount = availableBerthCount;
            const addonCount = availableAddonCount;
            const serviceCount = filteredEvents.filter(
              (e) => !!e.service && !e.isAvailable && !e.isBlocked
            ).length;
            const total = berthCount + addonCount + serviceCount;

            return (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-gradient-to-r from-blue-500 to-blue-600"></div>
                    <span className="text-sm font-medium text-gray-700  contrast:text-gray-500">
                      Berths
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="text-lg font-bold text-gray-900 dark:text-white contrast:text-white">
                      {berthCount}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-700 contrast:text-gray-500 ml-1">
                      {formatPercent(
                        total > 0 ? Math.round((berthCount / total) * 100) : 0
                      )}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                    <span className="text-sm font-medium text-gray-700  contrast:text-gray-500">
                      Add-ons
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="text-lg font-bold text-gray-900 dark:text-white contrast:text-white">
                      {addonCount}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-700 contrast:text-gray-500 ml-1">
                      {formatPercent(
                        total > 0 ? Math.round((addonCount / total) * 100) : 0
                      )}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                    <span className="text-sm font-medium text-gray-700  contrast:text-gray-500">
                      Services
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="text-lg font-bold text-gray-900 dark:text-white contrast:text-white">
                      {serviceCount}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-700 contrast:text-gray-500 ml-1">
                      {formatPercent(
                        total > 0 ? Math.round((serviceCount / total) * 100) : 0
                      )}
                    </span>
                  </div>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-600 contrast:bg-gray-600 h-2 rounded-full overflow-hidden">
                  <div className="h-full flex">
                    <div
                      className="bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-500"
                      style={{
                        width: `${total > 0 ? (berthCount / total) * 100 : 0}%`,
                      }}
                    />
                    <div
                      className="bg-purple-500 transition-all duration-500"
                      style={{
                        width: `${total > 0 ? (addonCount / total) * 100 : 0}%`,
                      }}
                    />
                    <div
                      className="bg-orange-500 transition-all duration-500"
                      style={{
                        width: `${total > 0 ? (serviceCount / total) * 100 : 0}%`,
                      }}
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-700 contrast:text-gray-500">
                  Berths & add-ons reflect available assets; services reflect active bookings.
                </p>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Addon Status Card */}
      <div className="group rounded-2xl shadow-lg hover:shadow-xl border border-gray-100 dark:border-gray-700 contrast:border-gray-700 overflow-hidden">
        <div className="bg-blue-100 dark:bg-blue-950 contrast:bg-blue-950 p-4">
          <div className="flex justify-between items-start">
            <div className="text-gray-900">
              <p className="text-gray-900 text-[16px] font-[500]">
                Add-on Status
              </p>
            </div>
            <div className="p-3 bg-blue-300 dark:bg-blue-700 contrast:bg-blue-700 rounded-xl backdrop-blur-sm">
              <Package className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>
        <div className="p-4">
          {(() => {
            const allAddons = filteredEvents.filter(
              (e) => e.assetType === 'addon'
            );
            const availableCount = allAddons.filter(
              (e) => e.status === 'available'
            ).length;
            const blockedCount = allAddons.filter(
              (e) => e.status === 'blocked'
            ).length;
            const bookingCount = allAddons.filter(
              (e) => !e.isAvailable && !e.isBlocked
            ).length;
            const total = allAddons.length || 1;

            return (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <span className="text-sm font-medium text-gray-700  contrast:text-gray-500">
                      Available
                    </span>
                  </div>
                  <span className="text-lg font-bold text-gray-900 dark:text-white contrast:text-white">
                    {availableCount}
                    <span className="text-xs text-gray-500 dark:text-gray-700 contrast:text-gray-500 ml-1">
                      ({formatPercent(Math.round((availableCount / total) * 100))})
                    </span>
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <span className="text-sm font-medium text-gray-700  contrast:text-gray-500">
                      Blocked
                    </span>
                  </div>
                  <span className="text-lg font-bold text-gray-900 dark:text-white contrast:text-white">
                    {blockedCount}
                    <span className="text-xs text-gray-500 dark:text-gray-700 contrast:text-gray-500 ml-1">
                      ({formatPercent(Math.round((blockedCount / total) * 100))})
                    </span>
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    <span className="text-sm font-medium text-gray-700  contrast:text-gray-500">
                      Bookings
                    </span>
                  </div>
                  <span className="text-lg font-bold text-gray-900 dark:text-white contrast:text-white">
                    {bookingCount}
                    <span className="text-xs text-gray-500 dark:text-gray-700 contrast:text-gray-500 ml-1">
                      ({formatPercent(Math.round((bookingCount / total) * 100))})
                    </span>
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-600 contrast:bg-gray-600 h-2 rounded-full overflow-hidden">
                  <div className="h-full flex">
                    <div
                      className="bg-green-500 transition-all duration-500"
                      style={{
                        width: `${(availableCount / total) * 100}%`,
                      }}
                    />
                    <div
                      className="bg-red-500 transition-all duration-500"
                      style={{
                        width: `${(blockedCount / total) * 100}%`,
                      }}
                    />
                    <div
                      className="bg-blue-500 transition-all duration-500"
                      style={{
                        width: `${(bookingCount / total) * 100}%`,
                      }}
                    />
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default StatusCards;
